import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { accessControlAction } from '@/lib/api';
import {
  FUNCTIONS_BASE,
  getBankIdHeaders,
  createBankIdUser,
  linkBankIdVerification,
  BANKID_ERROR_MESSAGES,
  getErrorMessage,
} from '@/lib/bankid';

/**
 * BankIdCallbackPage — polls /api-auth/bankid/verify-only after BankID redirect.
 * Same flow as pwa-handel BankIdCallbackPage, adapted for pwa-atkomst.
 *
 * Inloggning:
 *  - Befintlig användare: sätter Supabase-session och går till /keys.
 *  - Ny användare: skapar kontot via /bankid/create-user och sätter sessionen.
 *    Hittar kontot varken inbjudningar eller åtkomster visas /valkommen med
 *    vägledning i stället för en tom app.
 *
 * Länkning (mode=link, startad från profilsidan av en INLOGGAD användare):
 *  - Byter ALDRIG session — kopplar i stället BankID-identiteten till det
 *    inloggade kontot via api-bankid-link och återvänder till /profile.
 */
export default function BankIdCallbackPage() {
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState('Verifierar BankID...');

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlOrderRef = params.get('oref');
      const storedOrderRef = sessionStorage.getItem('bankid_orderRef');
      const isLinkMode = params.get('mode') === 'link'
        || sessionStorage.getItem('bankid_link_mode') === 'true';

      // Use stored orderRef if URL differs (newer session overrides stale redirect)
      const orderRef = storedOrderRef || urlOrderRef;

      if (!orderRef) {
        navigate(isLinkMode ? '/profile' : '/auth', { replace: true });
        return;
      }

      // Vid fel: länk-läget återvänder till profilen (användaren är inloggad),
      // inloggningsläget till inloggningssidan.
      const fallbackPath = isLinkMode ? '/profile' : '/auth';

      // Check if orderRef is too old (BankID 180s timeout)
      const orderRefTimestamp = sessionStorage.getItem('bankid_orderRef_timestamp');
      if (orderRefTimestamp) {
        const age = Date.now() - parseInt(orderRefTimestamp, 10);
        if (age > 180000) {
          sessionStorage.removeItem('bankid_orderRef');
          sessionStorage.removeItem('bankid_orderRef_timestamp');
          setStatusMessage(BANKID_ERROR_MESSAGES.expired);
          setTimeout(() => navigate(fallbackPath, { replace: true }), 1500);
          return;
        }
      }

      let pollCount = 0;
      let consecutiveErrors = 0;
      const MAX_POLLS = 45;
      const MAX_CONSECUTIVE_ERRORS = 5;
      const POLL_INTERVAL = 2000;

      const poll = async () => {
        pollCount++;

        if (pollCount > MAX_POLLS) {
          setStatusMessage(BANKID_ERROR_MESSAGES.timeout);
          setTimeout(() => navigate(fallbackPath, { replace: true }), 2000);
          return;
        }

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 12000);

          const response = await fetch(`${FUNCTIONS_BASE}/api-auth/bankid/verify-only`, {
            method: 'POST',
            headers: getBankIdHeaders(),
            body: JSON.stringify({ orderRef }),
            signal: controller.signal,
          });

          clearTimeout(timeoutId);
          consecutiveErrors = 0;

          const data = await response.json();

          if (data.status === 'complete') {
            setStatusMessage('BankID verifierad!');
            sessionStorage.removeItem('bankid_orderRef');
            sessionStorage.removeItem('bankid_orderRef_timestamp');
            sessionStorage.removeItem('bankid_link_mode');

            if (isLinkMode) {
              // LÄNK-LÄGE: koppla identiteten till det redan inloggade kontot.
              // Sätt aldrig sessionen från verify-only-svaret här — det vore
              // ett kontobyte, inte en koppling.
              const { data: sessionData } = await supabase.auth.getSession();
              const session = sessionData?.session;

              if (!session) {
                setStatusMessage('Du är inte inloggad längre. Logga in och försök igen.');
                setTimeout(() => navigate('/auth', { replace: true }), 2500);
                return;
              }

              if (data.already_registered) {
                setStatusMessage(
                  data.user_id === session.user.id
                    ? 'BankID är redan kopplat till ditt konto.'
                    : 'Detta BankID är redan kopplat till ett annat konto.'
                );
                setTimeout(() => navigate('/profile', { replace: true }), 2500);
                return;
              }

              try {
                await linkBankIdVerification(data.verification_token, session.access_token);
                setStatusMessage('BankID kopplat till ditt konto!');
                setTimeout(() => navigate('/profile', { replace: true }), 1500);
              } catch (linkErr) {
                setStatusMessage(
                  linkErr instanceof Error ? linkErr.message : 'Kunde inte koppla BankID'
                );
                setTimeout(() => navigate('/profile', { replace: true }), 3000);
              }
              return;
            }

            if (data.already_registered) {
              // EXISTING USER — set Supabase session from tokens
              if (!data.supabase_access_token || !data.supabase_refresh_token) {
                setStatusMessage(getErrorMessage('technical_error'));
                setTimeout(() => navigate(fallbackPath, { replace: true }), 2000);
                return;
              }

              const { error: sessionError } = await supabase.auth.setSession({
                access_token: data.supabase_access_token,
                refresh_token: data.supabase_refresh_token,
              });

              if (sessionError) {
                setStatusMessage(getErrorMessage('technical_error'));
                setTimeout(() => navigate(fallbackPath, { replace: true }), 2000);
                return;
              }

              navigate('/keys', { replace: true });
            } else {
              // NEW USER — skapa kontot och logga in direkt. Inbjudningar
              // (t.ex. på personnummer) matchas först när kontot finns.
              setStatusMessage('Skapar ditt konto...');

              try {
                const tokens = await createBankIdUser(data.verification_token, data.display_name);

                const { error: sessionError } = await supabase.auth.setSession({
                  access_token: tokens.accessToken,
                  refresh_token: tokens.refreshToken,
                });

                if (sessionError) {
                  setStatusMessage(getErrorMessage('technical_error'));
                  setTimeout(() => navigate(fallbackPath, { replace: true }), 2000);
                  return;
                }

                // Helt nytt konto utan väntande inbjudningar = personen är
                // sannolikt inbjuden via e-post/telefon och har hamnat fel.
                // Visa vägledning i stället för en tom app.
                try {
                  const invitations = await accessControlAction<{ invitations: unknown[] }>(
                    'list-my-invitations'
                  );
                  if ((invitations.data?.invitations ?? []).length === 0) {
                    navigate('/valkommen', { replace: true });
                    return;
                  }
                } catch {
                  // Kunde inte läsa inbjudningar — fortsätt till nycklarna
                }

                navigate('/keys', { replace: true });
              } catch {
                setStatusMessage(getErrorMessage('technical_error'));
                setTimeout(() => navigate(fallbackPath, { replace: true }), 2500);
              }
            }
            return;
          } else if (data.status === 'failed') {
            // Handle invalidParameters/noSuchOrder immediately
            if (data.hintCode === 'invalidParameters' || data.hintCode === 'noSuchOrder') {
              sessionStorage.removeItem('bankid_orderRef');
              sessionStorage.removeItem('bankid_orderRef_timestamp');
              setStatusMessage('BankID-sessionen har gått ut. Försök igen.');
              setTimeout(() => navigate(fallbackPath, { replace: true }), 1500);
              return;
            }

            sessionStorage.removeItem('bankid_orderRef');
            sessionStorage.removeItem('bankid_orderRef_timestamp');
            setStatusMessage(getErrorMessage(data.hintCode, data.message));
            setTimeout(() => navigate(fallbackPath, { replace: true }), 2500);
            return;
          } else {
            // pending — continue polling
            setTimeout(poll, POLL_INTERVAL);
          }
        } catch (err: unknown) {
          const error = err as Error;
          consecutiveErrors++;

          if (error?.name === 'AbortError') {
            setTimeout(poll, POLL_INTERVAL);
            return;
          }

          if (consecutiveErrors < MAX_CONSECUTIVE_ERRORS) {
            setTimeout(poll, POLL_INTERVAL * (consecutiveErrors + 1));
            return;
          }

          setStatusMessage(getErrorMessage('technical_error'));
          setTimeout(() => navigate(fallbackPath, { replace: true }), 2000);
        }
      };

      poll();
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-foreground/80 text-center">{statusMessage}</p>
      </div>
    </div>
  );
}
